import React, { useEffect, useState, useMemo } from 'react';
import {
  Plus,
  Save,
  Trash2,
  Printer,
  Copy,
  Utensils,
  Search,
  Check,
  X,
  Sparkles,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { exportMealPlanToPdf } from './MealPlanPdfExport';

const MEAL_SLOTS = [
  { id: 'breakfast', label: '🌅 Breakfast / Desayuno', defaultTime: '08:00' },
  { id: 'mid_morning', label: '🍏 Mid-Morning / Media Mañana', defaultTime: '11:00' },
  { id: 'lunch', label: '🥗 Lunch / Comida', defaultTime: '14:00' },
  { id: 'snack', label: '🥜 Afternoon Snack / Merienda', defaultTime: '17:30' },
  { id: 'dinner', label: '🌙 Dinner / Cena', defaultTime: '20:30' },
  { id: 'workout', label: '⚡ Pre / Post Workout', defaultTime: '18:30' },
];

const createDefaultDay = (index = 1) => ({
  id: `day-${Date.now()}-${index}`,
  label: `Day ${index}`,
  meals: [
    { id: `m-b-${Date.now()}`, slot: 'breakfast', time: '08:00', notes: '', items: [] },
    { id: `m-l-${Date.now()}`, slot: 'lunch', time: '14:00', notes: '', items: [] },
    { id: `m-s-${Date.now()}`, slot: 'snack', time: '17:30', notes: '', items: [] },
    { id: `m-d-${Date.now()}`, slot: 'dinner', time: '20:30', notes: '', items: [] },
  ],
});

const MealPlanBuilder = ({ patient = null }) => {
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [foods, setFoods] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Food picker modal state
  const [pickerMealIdx, setPickerMealIdx] = useState(null);
  const [foodQuery, setFoodQuery] = useState('');
  const [foodCategory, setFoodCategory] = useState('all');
  const [selectedFood, setSelectedFood] = useState(null);
  const [gramsInput, setGramsInput] = useState(100);
  const [portionLabelInput, setPortionLabelInput] = useState('');
  const [alternativesInput, setAlternativesInput] = useState('');

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [allPlans, foodList] = await Promise.all([
        adminApi.getMealPlans(patient ? { patientId: patient.id } : {}),
        adminApi.getFoods(),
      ]);
      const tplList = patient
        ? await adminApi.getMealPlans({ isTemplate: true })
        : allPlans.filter((p) => p.is_template);

      setPlans(allPlans);
      setTemplates(tplList);
      setFoods(foodList);

      if (allPlans.length > 0) {
        setActivePlan(allPlans[0]);
        setActiveDayIdx(0);
      } else {
        setActivePlan(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  const startNewPlan = (fromTemplate = null) => {
    const baseLang = patient?.preferred_language || fromTemplate?.language || 'es';
    const baseKcal = patient?.target_kcal || fromTemplate?.target_kcal || 1800;
    const baseMacros = patient?.target_macros ||
      fromTemplate?.target_macros || {
        protein_g: 115,
        carbs_g: 175,
        fat_g: 65,
        fiber_g: 30,
      };

    const clonedDays = fromTemplate?.days
      ? JSON.parse(JSON.stringify(fromTemplate.days))
      : [createDefaultDay(1)];

    const draft = {
      id: null,
      patient_id: patient ? patient.id : null,
      is_template: !patient,
      title: fromTemplate
        ? `${fromTemplate.title}${patient ? ` – ${patient.full_name}` : ' (Copy)'}`
        : patient
        ? `Plan Nutricional – ${patient.full_name}`
        : 'New Meal Plan Template',
      language: baseLang,
      status: 'active',
      target_kcal: baseKcal,
      target_macros: { ...baseMacros },
      days: clonedDays,
      general_guidelines:
        fromTemplate?.general_guidelines ||
        '• Drink 2.0–2.5L of water daily.\n• Prioritize Extra Virgin Olive Oil (EVOO) for dressing.\n• Eat mindfully and avoid screens during meals.',
    };
    setActivePlan(draft);
    setActiveDayIdx(0);
  };

  const handleSavePlan = async () => {
    if (!activePlan) return;
    setSaving(true);
    try {
      const payload = {
        patient_id: activePlan.patient_id,
        is_template: Boolean(activePlan.is_template),
        title: activePlan.title,
        language: activePlan.language,
        status: activePlan.status || 'active',
        target_kcal: Number(activePlan.target_kcal) || 1800,
        target_macros: activePlan.target_macros,
        days: activePlan.days,
        general_guidelines: activePlan.general_guidelines,
      };

      let saved;
      if (activePlan.id) {
        saved = await adminApi.updateMealPlan(activePlan.id, payload);
        setPlans((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      } else {
        saved = await adminApi.createMealPlan(payload);
        setPlans((prev) => [saved, ...prev]);
      }
      setActivePlan(saved);
      flash('Meal plan saved!');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!planId) {
      setActivePlan(plans[0] || null);
      return;
    }
    if (!window.confirm('Delete this meal plan?')) return;
    await adminApi.deleteMealPlan(planId);
    const remaining = plans.filter((p) => p.id !== planId);
    setPlans(remaining);
    setActivePlan(remaining[0] || null);
    flash('Meal plan deleted.');
  };

  // ── Day & Meal manipulation ──
  const currentDay = activePlan?.days?.[activeDayIdx] || null;

  const updateCurrentDay = (updater) => {
    setActivePlan((prev) => {
      if (!prev) return prev;
      const nextDays = (prev.days || []).map((d, i) => (i === activeDayIdx ? updater(d) : d));
      return { ...prev, days: nextDays };
    });
  };

  const addDay = () => {
    if (!activePlan) return;
    const nextIdx = (activePlan.days || []).length + 1;
    const newDay = createDefaultDay(nextIdx);
    setActivePlan((prev) => ({ ...prev, days: [...(prev.days || []), newDay] }));
    setActiveDayIdx(nextIdx - 1);
  };

  const removeDay = (idx) => {
    if (!activePlan || (activePlan.days || []).length <= 1) return;
    const nextDays = activePlan.days.filter((_, i) => i !== idx);
    setActivePlan((prev) => ({ ...prev, days: nextDays }));
    setActiveDayIdx(Math.max(0, idx - 1));
  };

  const addMealSlot = (slotId = 'snack') => {
    const slotMeta = MEAL_SLOTS.find((s) => s.id === slotId) || MEAL_SLOTS[0];
    updateCurrentDay((day) => ({
      ...day,
      meals: [
        ...(day.meals || []),
        {
          id: `meal-${Date.now()}`,
          slot: slotMeta.id,
          time: slotMeta.defaultTime,
          notes: '',
          items: [],
        },
      ],
    }));
  };

  const removeMealSlot = (mealIdx) => {
    updateCurrentDay((day) => ({
      ...day,
      meals: day.meals.filter((_, i) => i !== mealIdx),
    }));
  };

  const updateMealField = (mealIdx, field, val) => {
    updateCurrentDay((day) => ({
      ...day,
      meals: day.meals.map((m, i) => (i === mealIdx ? { ...m, [field]: val } : m)),
    }));
  };

  const removeFoodFromMeal = (mealIdx, itemIdx) => {
    updateCurrentDay((day) => ({
      ...day,
      meals: day.meals.map((m, i) =>
        i === mealIdx ? { ...m, items: m.items.filter((_, j) => j !== itemIdx) } : m
      ),
    }));
  };

  const updateItemGrams = (mealIdx, itemIdx, newGrams) => {
    const g = Math.max(1, Number(newGrams) || 0);
    updateCurrentDay((day) => ({
      ...day,
      meals: day.meals.map((m, i) => {
        if (i !== mealIdx) return m;
        return {
          ...m,
          items: m.items.map((it, j) => {
            if (j !== itemIdx) return it;
            const prevG = Number(it.grams) || 100;
            const ratio = g / prevG;
            return {
              ...it,
              grams: g,
              portion_label: `${g}g`,
              kcal: Math.round((it.kcal || 0) * ratio),
              protein_g: Number(((it.protein_g || 0) * ratio).toFixed(1)),
              carbs_g: Number(((it.carbs_g || 0) * ratio).toFixed(1)),
              fat_g: Number(((it.fat_g || 0) * ratio).toFixed(1)),
              fiber_g: Number(((it.fiber_g || 0) * ratio).toFixed(1)),
            };
          }),
        };
      }),
    }));
  };

  // ── Food Picker Modal handlers ──
  const openFoodPicker = (mealIdx) => {
    setPickerMealIdx(mealIdx);
    setFoodQuery('');
    setSelectedFood(null);
    setGramsInput(100);
    setPortionLabelInput('');
    setAlternativesInput('');
  };

  const selectFoodInModal = (food) => {
    setSelectedFood(food);
    const g = food.default_portion_grams || 100;
    setGramsInput(g);
    setPortionLabelInput(food.default_portion_name || `${g}g`);
  };

  const confirmAddFoodToMeal = (e) => {
    e.preventDefault();
    if (!selectedFood || pickerMealIdx === null) return;
    const g = Number(gramsInput) || 100;
    const factor = g / 100.0;
    const lang = activePlan?.language || 'es';
    const localizedName =
      selectedFood.name_i18n?.[lang] ||
      selectedFood.name_i18n?.en ||
      selectedFood.name_i18n?.es ||
      'Food';

    const newItem = {
      id: `item-${Date.now()}`,
      food_id: selectedFood.id,
      name: localizedName,
      category: selectedFood.category || 'Produce',
      grams: g,
      portion_label: portionLabelInput.trim() || `${g}g`,
      kcal: Math.round((selectedFood.kcal_per_100g || 0) * factor),
      protein_g: Number(((selectedFood.protein_per_100g || 0) * factor).toFixed(1)),
      carbs_g: Number(((selectedFood.carbs_per_100g || 0) * factor).toFixed(1)),
      fat_g: Number(((selectedFood.fat_per_100g || 0) * factor).toFixed(1)),
      fiber_g: Number(((selectedFood.fiber_per_100g || 0) * factor).toFixed(1)),
      alternatives: alternativesInput.trim(),
    };

    updateCurrentDay((day) => ({
      ...day,
      meals: day.meals.map((m, i) =>
        i === pickerMealIdx ? { ...m, items: [...(m.items || []), newItem] } : m
      ),
    }));
    setPickerMealIdx(null);
  };

  // ── Live Day Nutritional Totals ──
  const dayTotals = useMemo(() => {
    const totals = { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 };
    if (!currentDay) return totals;
    (currentDay.meals || []).forEach((meal) => {
      (meal.items || []).forEach((it) => {
        totals.kcal += Number(it.kcal) || 0;
        totals.protein_g += Number(it.protein_g) || 0;
        totals.carbs_g += Number(it.carbs_g) || 0;
        totals.fat_g += Number(it.fat_g) || 0;
        totals.fiber_g += Number(it.fiber_g) || 0;
      });
    });
    return {
      kcal: Math.round(totals.kcal),
      protein_g: Math.round(totals.protein_g),
      carbs_g: Math.round(totals.carbs_g),
      fat_g: Math.round(totals.fat_g),
      fiber_g: Math.round(totals.fiber_g),
    };
  }, [currentDay]);

  const filteredFoods = useMemo(() => {
    return foods.filter((f) => {
      if (foodCategory !== 'all' && f.category !== foodCategory) return false;
      if (!foodQuery.trim()) return true;
      const q = foodQuery.trim().toLowerCase();
      return (
        (f.name_i18n?.en || '').toLowerCase().includes(q) ||
        (f.name_i18n?.es || '').toLowerCase().includes(q) ||
        (f.name_i18n?.it || '').toLowerCase().includes(q)
      );
    });
  }, [foods, foodCategory, foodQuery]);

  if (loading) {
    return <p className="text-sm text-gray-400 py-10 text-center">Loading meal plan builder…</p>;
  }

  return (
    <div className="space-y-6">
      {feedback && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {feedback}
        </div>
      )}

      {/* Top Toolbar: Select existing plan, clone template, or create new */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
            <Utensils className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">
              {patient ? `Meal Plans for ${patient.full_name}` : 'Meal Plans & Diet Templates'}
            </h3>
            <p className="text-xs text-gray-500">
              Build interactive daily/weekly menus with real-time macro tracking and 1-click PDF export.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {plans.length > 0 && (
            <select
              value={activePlan?.id || ''}
              onChange={(e) => {
                const found = plans.find((p) => p.id === e.target.value);
                if (found) {
                  setActivePlan(found);
                  setActiveDayIdx(0);
                }
              }}
              className="border border-gray-300 rounded-xl px-3 py-2 text-sm font-medium bg-white"
            >
              {!activePlan?.id && <option value="">• Unsaved Draft</option>}
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.is_template ? '📋 [Template] ' : '🥗 '}
                  {p.title} ({p.target_kcal} kcal)
                </option>
              ))}
            </select>
          )}

          {templates.length > 0 && (
            <div className="relative">
              <select
                value=""
                onChange={(e) => {
                  const tpl = templates.find((t) => t.id === e.target.value);
                  if (tpl) startNewPlan(tpl);
                }}
                className="border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-xl px-3 py-2 text-xs font-bold cursor-pointer"
              >
                <option value="">✨ Clone from Template…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title} ({t.target_kcal} kcal)
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            onClick={() => startNewPlan(null)}
            className="inline-flex items-center gap-1.5 bg-gray-900 hover:bg-primary text-white text-xs font-bold px-3.5 py-2.5 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Blank Plan
          </button>
        </div>
      </div>

      {!activePlan ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center space-y-4">
          <p className="text-gray-500 text-sm">
            No meal plan selected yet. Start from a clinical template or create a blank plan.
          </p>
          <div className="flex justify-center gap-3">
            {templates[0] && (
              <button
                type="button"
                onClick={() => startNewPlan(templates[0])}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl"
              >
                <Copy className="w-4 h-4" />
                Load "{templates[0].title}"
              </button>
            )}
            <button
              type="button"
              onClick={() => startNewPlan(null)}
              className="inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-xl"
            >
              <Plus className="w-4 h-4" />
              Create Blank Meal Plan
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
          {/* Plan Metadata & Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end border-b border-gray-100 pb-5">
            <div className="lg:col-span-5">
              <label className="block text-xs font-bold uppercase text-gray-400 mb-1">
                Meal Plan Title
              </label>
              <input
                type="text"
                value={activePlan.title}
                onChange={(e) => setActivePlan({ ...activePlan, title: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm font-bold text-gray-900"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-xs font-bold uppercase text-gray-400 mb-1">
                PDF Language
              </label>
              <select
                value={activePlan.language || 'es'}
                onChange={(e) => setActivePlan({ ...activePlan, language: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-semibold bg-white"
              >
                <option value="es">🇪🇸 Español</option>
                <option value="en">🇬🇧 English</option>
                <option value="it">🇮🇹 Italiano</option>
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-xs font-bold uppercase text-gray-400 mb-1">
                Target kcal
              </label>
              <input
                type="number"
                step="50"
                value={activePlan.target_kcal}
                onChange={(e) =>
                  setActivePlan({ ...activePlan, target_kcal: Number(e.target.value) || 1800 })
                }
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-bold text-gray-900"
              />
            </div>

            <div className="lg:col-span-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleSavePlan}
                disabled={saving}
                className="inline-flex items-center gap-1.5 bg-primary hover:bg-blue-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save Plan'}
              </button>
              <button
                type="button"
                onClick={() => exportMealPlanToPdf(activePlan, patient)}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
                title="Generate printable PDF & shopping list"
              >
                <Printer className="w-4 h-4" />
                PDF & Grocery List
              </button>
              <button
                type="button"
                onClick={() => handleDeletePlan(activePlan.id)}
                className="p-2 text-gray-400 hover:text-red-600 rounded-xl hover:bg-red-50"
                title="Delete plan"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Live Macro Comparison Bar (Planned vs Target) */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              {
                label: 'Calories',
                actual: dayTotals.kcal,
                target: activePlan.target_kcal || 1800,
                unit: 'kcal',
                barColor: 'bg-emerald-500',
              },
              {
                label: 'Protein',
                actual: dayTotals.protein_g,
                target: activePlan.target_macros?.protein_g || 115,
                unit: 'g',
                barColor: 'bg-blue-500',
              },
              {
                label: 'Carbs',
                actual: dayTotals.carbs_g,
                target: activePlan.target_macros?.carbs_g || 175,
                unit: 'g',
                barColor: 'bg-amber-500',
              },
              {
                label: 'Healthy Fats',
                actual: dayTotals.fat_g,
                target: activePlan.target_macros?.fat_g || 65,
                unit: 'g',
                barColor: 'bg-purple-500',
              },
              {
                label: 'Fiber',
                actual: dayTotals.fiber_g,
                target: activePlan.target_macros?.fiber_g || 30,
                unit: 'g',
                barColor: 'bg-teal-500',
              },
            ].map((m) => {
              const pct = Math.min(100, Math.round((m.actual / Math.max(1, m.target)) * 100));
              return (
                <div key={m.label} className="bg-white rounded-xl p-3 border border-gray-100">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span className="font-bold text-gray-700">{m.label}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="text-sm font-extrabold text-gray-900 mb-1.5">
                    {m.actual}{' '}
                    <span className="text-xs font-normal text-gray-400">
                      / {m.target} {m.unit}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${m.barColor} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Day Selector Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
            <div className="flex flex-wrap items-center gap-2">
              {(activePlan.days || []).map((day, idx) => (
                <button
                  key={day.id || idx}
                  type="button"
                  onClick={() => setActiveDayIdx(idx)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
                    activeDayIdx === idx
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {day.label || `Day ${idx + 1}`}
                </button>
              ))}
              <button
                type="button"
                onClick={addDay}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-primary bg-blue-50 hover:bg-blue-100"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Day
              </button>
            </div>

            {currentDay && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={currentDay.label || ''}
                  onChange={(e) =>
                    updateCurrentDay((d) => ({ ...d, label: e.target.value }))
                  }
                  placeholder="Day label (e.g. Monday – Friday)"
                  className="border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-700"
                />
                {(activePlan.days || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDay(activeDayIdx)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Remove Day
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Meals List for Active Day */}
          {currentDay && (
            <div className="space-y-4">
              {(currentDay.meals || []).map((meal, mealIdx) => (
                <div
                  key={meal.id || mealIdx}
                  className="border border-gray-200 rounded-2xl overflow-hidden bg-white"
                >
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={meal.slot}
                        onChange={(e) => updateMealField(mealIdx, 'slot', e.target.value)}
                        className="bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-bold text-gray-900"
                      >
                        {MEAL_SLOTS.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>

                      <input
                        type="time"
                        value={meal.time || '08:00'}
                        onChange={(e) => updateMealField(mealIdx, 'time', e.target.value)}
                        className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold text-gray-700"
                      />

                      <input
                        type="text"
                        value={meal.notes || ''}
                        onChange={(e) => updateMealField(mealIdx, 'notes', e.target.value)}
                        placeholder="Meal note / coaching tip…"
                        className="bg-white border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-600 w-56 sm:w-72"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openFoodPicker(mealIdx)}
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Food / Recipe
                      </button>
                      <button
                        type="button"
                        onClick={() => removeMealSlot(mealIdx)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Remove meal slot"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Food Items inside Meal */}
                  {(meal.items || []).length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">
                      No foods added yet. Click "+ Add Food / Recipe" to build this meal.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {meal.items.map((item, itemIdx) => (
                        <div
                          key={item.id || itemIdx}
                          className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50/60"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm text-gray-900">{item.name}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                                {item.portion_label || `${item.grams}g`}
                              </span>
                            </div>
                            {item.alternatives && (
                              <p className="text-xs text-emerald-700 mt-0.5">
                                ↻ Swap option: {item.alternatives}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1 bg-white">
                              <input
                                type="number"
                                min="5"
                                step="5"
                                value={item.grams || 100}
                                onChange={(e) => updateItemGrams(mealIdx, itemIdx, e.target.value)}
                                className="w-14 text-right text-xs font-bold text-gray-800 focus:outline-none"
                              />
                              <span className="text-xs text-gray-400 ml-1">g</span>
                            </div>

                            <div className="text-xs text-gray-500 font-medium min-w-[175px] text-right">
                              <strong className="text-gray-800">{Math.round(item.kcal || 0)} kcal</strong>{' '}
                              · P:{item.protein_g}g · C:{item.carbs_g}g · F:{item.fat_g}g
                            </div>

                            <button
                              type="button"
                              onClick={() => removeFoodFromMeal(mealIdx, itemIdx)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Remove food"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => addMealSlot('snack')}
                className="w-full py-2.5 border-2 border-dashed border-gray-200 hover:border-primary rounded-2xl text-xs font-bold text-gray-500 hover:text-primary transition-colors flex items-center justify-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Add Another Meal Slot (Snack / Pre-Workout / Supper)
              </button>
            </div>
          )}

          {/* General Clinical & Hydration Guidelines */}
          <div className="pt-4 border-t border-gray-100">
            <label className="block text-xs font-bold uppercase text-gray-500 mb-1.5">
              General Clinical Guidelines, Hydration & Supplement Notes (Included on PDF)
            </label>
            <textarea
              rows={3}
              value={activePlan.general_guidelines || ''}
              onChange={(e) =>
                setActivePlan({ ...activePlan, general_guidelines: e.target.value })
              }
              className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-800"
              placeholder="Hydration target, meal timing, olive oil allowance, supplements…"
            />
          </div>
        </div>
      )}

      {/* Food & Recipe Picker Modal */}
      {pickerMealIdx !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-2xl w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                Add Food or Recipe to Meal
              </h3>
              <button
                type="button"
                onClick={() => setPickerMealIdx(null)}
                className="p-1 text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={foodQuery}
                  onChange={(e) => setFoodQuery(e.target.value)}
                  placeholder="Search food in English, Español, or Italiano…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>
              <select
                value={foodCategory}
                onChange={(e) => setFoodCategory(e.target.value)}
                className="border border-gray-300 rounded-xl px-3 py-2 text-xs font-bold bg-white"
              >
                <option value="all">All Categories</option>
                <option value="Proteins">Proteins</option>
                <option value="Grains">Grains & Tubers</option>
                <option value="Legumes">Legumes</option>
                <option value="Produce">Vegetables & Greens</option>
                <option value="Fruits">Fruits</option>
                <option value="Dairy">Dairy & Alternatives</option>
                <option value="Fats">Healthy Fats & Nuts</option>
                <option value="Recipes">Signature Recipes</option>
              </select>
            </div>

            <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
              {filteredFoods.map((f) => {
                const lang = activePlan?.language || 'es';
                const name = f.name_i18n?.[lang] || f.name_i18n?.en || f.name_i18n?.es;
                const isSelected = selectedFood?.id === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => selectFoodInModal(f)}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between text-xs transition-colors ${
                      isSelected ? 'bg-emerald-50 font-bold text-emerald-900' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div>
                      <span className="text-sm font-semibold text-gray-900">{name}</span>
                      <span className="ml-2 text-xs text-gray-400">({f.category})</span>
                    </div>
                    <div className="text-gray-500">
                      {f.kcal_per_100g} kcal/100g · P:{f.protein_per_100g}g · C:{f.carbs_per_100g}g · F:
                      {f.fat_per_100g}g
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedFood && (
              <form onSubmit={confirmAddFoodToMeal} className="bg-gray-50 p-4 rounded-xl space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">
                      Quantity (grams)
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={gramsInput}
                      onChange={(e) => {
                        setGramsInput(e.target.value);
                        setPortionLabelInput(`${e.target.value}g`);
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-bold bg-white"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-gray-600 mb-1">
                      Household Portion Label (shown on PDF)
                    </label>
                    <input
                      type="text"
                      value={portionLabelInput}
                      onChange={(e) => setPortionLabelInput(e.target.value)}
                      placeholder="e.g. 1 fillet (150g) or 2 slices (70g)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1">
                    Food Swap / Equivalent Option (optional)
                  </label>
                  <input
                    type="text"
                    value={alternativesInput}
                    onChange={(e) => setAlternativesInput(e.target.value)}
                    placeholder="e.g. 160g Turkey breast OR 180g Firm tofu"
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs font-semibold text-emerald-800">
                    Calculated:{' '}
                    {Math.round((selectedFood.kcal_per_100g * (Number(gramsInput) || 0)) / 100)} kcal ·
                    P:{((selectedFood.protein_per_100g * (Number(gramsInput) || 0)) / 100).toFixed(1)}g
                    · C:{((selectedFood.carbs_per_100g * (Number(gramsInput) || 0)) / 100).toFixed(1)}g
                    · F:{((selectedFood.fat_per_100g * (Number(gramsInput) || 0)) / 100).toFixed(1)}g
                  </span>
                  <button
                    type="submit"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-lg"
                  >
                    + Add to Meal
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlanBuilder;
